import React, { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";

// @material-ui/core components
import { CircularProgress, Typography, Box } from "@material-ui/core";

// core components
import NotificationContext from "views/Components/Context.js";
import { CloudUpload, Create, DoneAll } from "@material-ui/icons";
import CreateCourseCard from "views/Components/CreateCourseCard";

import {
  fetchAllPodcastsAPI,
  createPodcastAPI,
  deletePodcastAPI,
  updatePodcastTagsAPI,
  cloneEntryAPI,
  inviteUserAPI,
} from "views/APIs/APIUtility";
import CreatePodcastModal from "views/Components/CreatePodastModal";
import AssetDashboard from "views/Components/AssetDashboard";
// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";

// Custom components
import { fetchImageAsFile } from "views/Dashboard/Course/CourseConstants";
import PodcastDetails from "../Models/PodcastDetails";

const useStyles = makeStyles({
  ...styles,
});

export default function PodcastDesign() {
  const asset_identifier = "podcast";
  const images = require.context("assets/img/create_cards_16_9/", true);
  const path = useRouteMatch().path;
  const classes = useStyles();

  const { showNotification } = React.useContext(NotificationContext);

  // state variables
  const [createPodcastModal, setCreatePodcastModal] = React.useState(false);
  const [newPodcastDetails, setNewPodcastDetails] = React.useState(
    new PodcastDetails()
  );
  const [iconImage, setIconImage] = React.useState();
  const [allPodcasts, setAllPodcasts] = React.useState([]);
  const [initialPodcasts, setInitialPodcasts] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });
  const [podcastCreationLoader, setPodcastCreationLoader] = React.useState(
    false
  );
  const [podcastAdded, setPodcastAdded] = React.useState(false);

  useEffect(() => {
    const fetchAllPodcasts = async () => {
      // Fetch all podcasts
      try {
        setLoading({ loading: true, message: "Loading Podcasts..." });
        const data = await fetchAllPodcastsAPI();
        const mappedData = data.map(_mapResourceToAppType);
        setAllPodcasts(mappedData);
        setInitialPodcasts(mappedData);
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchAllPodcasts();
  }, []);

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIconImage(file);
    }
  };

  const handleDeletePodcast = async (podcastData) => {
    const formData = new FormData();
    formData.append("podcast_id", podcastData._id);

    try {
      const data = await deletePodcastAPI(formData);
      setAllPodcasts(
        allPodcasts.filter((podcast) => podcast._id !== podcastData._id)
      );
      showNotification("Podcast deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleCloneCourse = async (courseData) => {
    try {
      setLoading({ loading: true, message: "Cloning Podcast..." });
      const formData = new FormData();
      formData.append("collection", "podcast_design");
      formData.append("id", courseData._id);

      const data = await cloneEntryAPI(formData);
      setAllPodcasts((prevCourses) => [
        ...prevCourses,
        _mapResourceToAppType(data),
      ]);
      setPodcastAdded(true);
      showNotification("Podcast cloned successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  const handleCreatePodcast = async (modulesAtCreation = true) => {
    setPodcastCreationLoader(true);
    const imageToUse =
      iconImage || (await fetchImageAsFile(images("./podcast.png")));
    // const imageToUse = iconImage || images("./qusandbox_logo_v2.5.png");
    const formData = new FormData();
    formData.append("podcast_name", newPodcastDetails.name);
    formData.append("podcast_description", newPodcastDetails.description);
    formData.append("podcast_image", imageToUse);
    formData.append("podcast_transcript", newPodcastDetails.podcastOutline);
    newPodcastDetails.uploadedFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const data = await createPodcastAPI(formData);
      window.open(`/create/podcastdesign/podcast/${data._id}`, "_blank");
      setAllPodcasts([...allPodcasts, _mapResourceToAppType(data)]);
      setPodcastAdded(true);
      showNotification("Podcast created successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setPodcastCreationLoader(false);
      handleCreatePodcastModalClose();
    }
  };

  const handleSaveLabel = async (data) => {
    try {
      const formData = new FormData();
      formData.append("podcast_id", data._id);
      if (data.tags.length === 0) {
        formData.append("tags", []);
      }
      // Append each tag separately
      else {
        data.tags.forEach((tag) => formData.append("tags", tag));
      }
      await updatePodcastTagsAPI(formData);
      setAllPodcasts((prevPodcast) =>
        prevPodcast.map((podcast) =>
          podcast._id === data._id ? { ...podcast, tags: data.tags } : podcast
        )
      );
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const requestData = {
        collection_type: asset_identifier,
        project_id: data._id,
        invitee_email: data.email,
      };

      const response = await inviteUserAPI(requestData);

      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allPodcasts.find(
        (asset) => asset._id === updatedProjectId
      );
      if (!updatedAsset) {
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };

      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" } // Update status if email matches
          : invitee
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allCourses state
      const updatedPodcasts = allPodcasts.map((podcast) =>
        podcast._id === updatedProjectId ? newAsset : podcast
      );

      setAllPodcasts(updatedPodcasts);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  const handleCreatePodcastModalClose = () => {
    setCreatePodcastModal(false);
    setIconImage(null);
    setNewPodcastDetails(new PodcastDetails());
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "In Design Phase":
        return <Create style={{ color: "#435966" }} />;
      case "Ready for Publishing":
        return <CloudUpload style={{ color: "#435966" }} />;
      case "Published":
        return <DoneAll style={{ color: "#435966" }} />;
      default:
        return null;
    }
  };

  const _mapResourceToAppType = (resource) => {
    // Destructure the resource to exclude course_name and course_description
    const {
      podcast_name,
      podcast_description,
      podcast_image,
      ...rest
    } = resource;

    return {
      ...rest, // Spread all remaining properties
      name: podcast_name, // Add name property
      description: podcast_description, // Add description property
      image: podcast_image, // Add image property
    };
  };

  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <CreatePodcastModal
        newPodcastDetails={newPodcastDetails}
        setNewPodcastDetails={setNewPodcastDetails}
        iconImage={iconImage}
        createPodcastModal={createPodcastModal}
        handleCreatePodcastModalClose={handleCreatePodcastModalClose}
        handleCreatePodcast={handleCreatePodcast}
        handleIconUpload={handleIconUpload}
        podcastCreationLoader={podcastCreationLoader}
      />

      <AssetDashboard
        path={path}
        welcomeTitle="Podcast Design Dashboard"
        welcomeSubtitle="Start by creating a new podcast or reviewing your existing podcasts."
        allAssets={allPodcasts}
        setAllAssets={setAllPodcasts}
        initialAssets={initialPodcasts}
        noAssetMessage="Create or Review your Podcasts to get started."
        linkPath="/podcast/"
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreatePodcastModal}
        createAssetButtonText="Create Podcast"
        handleInviteSend={handleInviteSend}
        handleSaveLabel={handleSaveLabel}
        asset_identifier={asset_identifier}
        handleDeleteAsset={handleDeletePodcast}
        handleCloneAsset={handleCloneCourse}
        assetAdded={podcastAdded}
        setAssetAdded={setPodcastAdded}
        setInitialAssets={setInitialPodcasts}
        workflowConfig={{
          type: "podcast",
          title: "Podcast Design Workflow",
          description: `Quickly generate podcasts by providing project details and guiding the dialogue generation process. `,
          steps: [
            {
              label: "1. Setup project",
              body:
                "Enter the podcast name and description to set the theme and context.",
            },
            {
              label: "2. Upload resources",
              body:
                "Upload background files, scripts, or reference material to inform the podcast dialogue.",
            },
            {
              label: "3. Generate dialogue",
              body:
                "Provide instructions to guide the system in creating a natural conversation between two speakers.",
            },
            {
              label: "4. Submit & create podcast",
              body:
                "Submit the project to generate the podcast. A complete dialogue-based audio artifact is created for you.",
            },
          ],
          videoUrl:
            "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/podcast-design-demo.mp4",
          examples: [
            {
              title: "Operational Risk Management in Action",
              description: "Podcast for generating awareness of the course.",
              link:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qu-podcast-design/6882438e1cce570d6eb948ba/podcast_audio/podcast_audio_1753367446.mp3",
              mediaUrl:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qu-course-design/6871087909d1432ebda64788/course_image/qucreate_logo.png",
              mediaAlt: "Operational Risk Management in Action",
            },
            {
              title: "AI Agents",
              description:
                "Podcast about how to build AI AGENTS in cloud native systems",
              link:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qu-podcast-design/68d186af045220ae7ff79e4b/podcast_audio/podcast_audio_1758561978.mp3",
              mediaUrl:
                "https://www.cyberark.com/wp-content/uploads/2025/03/ai-agents-collaborative-intelligence1.jpg",
              mediaAlt: "AI Agents",
            },
            {
              title: "Bond Pricing",
              description:
                "Podcast about how to understand and apply duration and convexity is crucial but be aware of the assumptions involved",
              link:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qu-podcast-design/6838b71b3828a3e5ba153768/podcast_audio/podcast_audio_1748547363.mp3",
              mediaUrl:
                "https://penobscotfa.com/wp-content/uploads/2019/07/bond-yield.png",
              mediaAlt: "Bond Pricing",
            },
          ],
        }}
      />
    </div>
  );
}
